import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GmailComposeModal from './GmailComposeModal';
import NegotiationModal from './NegotiationModal';

// The deal's email, from the signed-in user's Gmail: follow-up reminders that are due or scheduled,
// and every conversation feebe started on this deal with the agent's replies synced in.
//
// Reminders are personal (yours only). Conversations show for anyone who can manage the deal, but only
// the person whose mailbox a thread lives in can reply to it.

const fmtWhen = (iso) => {
  const d = new Date(iso);
  const days = Math.round((d.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 1 && days < 7) return `in ${days} days`;
  if (days < -1 && days > -7) return `${-days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const fmtStamp = (iso) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const nameOf = (s) => (String(s || '').match(/^\s*"?([^"<]+?)"?\s*</)?.[1] || String(s || '').replace(/<.*>/, '')).trim();

export default function DealEmailPanel({ deal, refreshKey, onChanged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [compose, setCompose] = useState(null);     // { mode, threadId?, followup? }
  const [counterThread, setCounterThread] = useState(null); // thread id the AI counter replies in
  const [open, setOpen] = useState({});             // thread key → expanded
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    fetch(`/api/gmail/deals/${deal.id}`).then(async r => {
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load email');
      setData(d); setError(null);
    }).catch(e => setError(e.message));
  }, [deal.id]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const sync = async () => {
    setSyncing(true);
    try { await fetch(`/api/gmail/deals/${deal.id}/sync`, { method: 'POST' }); load(); onChanged && onChanged(); }
    finally { setSyncing(false); }
  };
  const act = async (url, init) => {
    const r = await fetch(url, { headers: { 'Content-Type': 'application/json' }, ...init });
    if (!r.ok) alert((await r.json().catch(() => ({}))).error || 'That did not work');
    load();
  };
  const snooze = (f, days) => {
    const d = new Date(); d.setDate(d.getDate() + days); d.setHours(9, 0, 0, 0);
    act(`/api/gmail/followups/${f.id}`, { method: 'PATCH', body: JSON.stringify({ due_at: d.toISOString() }) });
  };

  if (error) return <div style={card}><div style={h2}>Email</div><div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: 8 }}>{error}</div></div>;
  if (!data) return <div style={card}><div style={h2}>Email</div><div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: 8 }}>Loading…</div></div>;

  const { connection, threads, followups, sync: syncState } = data;
  const openFollowups = followups.filter(f => f.status === 'pending' || f.status === 'due');
  const due = openFollowups.filter(f => f.status === 'due');
  const scheduled = openFollowups.filter(f => f.status === 'pending');

  return (
    <div id="email" style={{ ...card, scrollMarginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={h2}>Email</div>
        {connection.connected && (
          <div style={{ display: 'flex', gap: 6 }}>
            {threads.some(t => t.can_reply) && <button onClick={sync} disabled={syncing} style={btnSm} title={syncState?.last_synced_at ? `Last checked ${fmtStamp(syncState.last_synced_at)}` : ''}>{syncing ? 'Checking…' : 'Check replies'}</button>}
            <button onClick={() => setCompose({ mode: 'new' })} style={{ ...btnSm, background: 'var(--teal)', borderColor: 'var(--teal)', color: '#fff' }}>New email</button>
          </div>
        )}
      </div>

      {!connection.connected && (
        <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--navy)', border: '1px solid var(--navy-border)', marginBottom: threads.length ? 12 : 0 }}>
          <div style={{ fontSize: 12.5, color: 'var(--gray-100)', fontWeight: 600 }}>
            {connection.status === 'revoked' ? 'Your Gmail connection expired.' : 'Send offers from your Gmail.'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3, marginBottom: 8 }}>Attach the contract, track the agent's replies here, and get reminded to follow up.</div>
          <button onClick={() => navigate('/settings')} style={btnSm}>{connection.status === 'revoked' ? 'Reconnect Gmail' : 'Connect Gmail'}</button>
        </div>
      )}
      {syncState?.last_error && connection.connected && (
        <div style={{ fontSize: 11.5, color: '#B85450', marginBottom: 10 }}>Reply sync hit a problem: {syncState.last_error}</div>
      )}

      {due.map(f => (
        <div key={f.id} style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(201,162,39,0.10)', border: '1px solid rgba(201,162,39,0.35)', marginBottom: 8 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--gold)' }}>Follow-up due {fmtWhen(f.due_at)}</div>
          <div style={{ fontSize: 12, color: 'var(--gray-300)', marginTop: 2 }}>{f.note || `No reply from ${f.to_email || 'the agent'} yet.`}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setCompose({ mode: 'followup', followup: f })} style={{ ...btnSm, background: 'var(--teal)', borderColor: 'var(--teal)', color: '#fff' }}>Review draft &amp; send</button>
            <button onClick={() => snooze(f, 2)} style={btnSm}>Snooze 2 days</button>
            <button onClick={() => act(`/api/gmail/followups/${f.id}/dismiss`, { method: 'POST' })} style={btnSm}>Dismiss</button>
          </div>
        </div>
      ))}
      {scheduled.map(f => (
        <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, padding: '6px 0', borderBottom: '1px solid var(--navy-border)' }}>
          <span style={{ color: 'var(--gray-300)' }}>Follow-up reminder <b style={{ color: 'var(--white)' }}>{fmtWhen(f.due_at)}</b></span>
          <span style={{ display: 'flex', gap: 10 }}>
            <span onClick={() => setCompose({ mode: 'followup', followup: f })} style={link}>Send now</span>
            <span onClick={() => act(`/api/gmail/followups/${f.id}/dismiss`, { method: 'POST' })} style={{ ...link, color: 'var(--gray-500)' }}>Cancel</span>
          </span>
        </div>
      ))}

      {threads.length === 0 ? (
        connection.connected && <div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: openFollowups.length ? 10 : 0 }}>No emails on this deal yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: openFollowups.length ? 12 : 0 }}>
          {threads.map((t, i) => {
            const key = t.thread_id || `local-${i}`;
            const isOpen = open[key] ?? i === 0;
            const last = t.messages[t.messages.length - 1];
            const replies = t.messages.filter(m => m.direction === 'inbound').length;
            return (
              <div key={key} style={{ border: '1px solid var(--navy-border)', borderRadius: 8, overflow: 'hidden' }}>
                <div onClick={() => setOpen(o => ({ ...o, [key]: !isOpen }))} style={{ padding: '9px 11px', cursor: 'pointer', background: 'var(--navy)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject || '(no subject)'}</span>
                    <span style={{ fontSize: 11, color: 'var(--gray-500)', flexShrink: 0 }}>{fmtWhen(t.last_at)}</span>
                  </div>
                  <div style={{ fontSize: 11.5, marginTop: 2, color: t.awaiting_reply ? 'var(--gray-500)' : 'var(--teal)' }}>
                    {t.awaiting_reply ? 'Awaiting reply' : 'Agent replied'} · {t.messages.length} message{t.messages.length > 1 ? 's' : ''}{replies ? ` · ${replies} repl${replies > 1 ? 'ies' : 'y'}` : ''}
                    {!t.mine && t.mailbox_user_name ? ` · in ${t.mailbox_user_name}'s Gmail` : ''}
                  </div>
                </div>
                {isOpen && (
                  <div>
                    {t.messages.map(m => (
                      <div key={m.id} style={{ padding: '9px 11px', borderTop: '1px solid var(--navy-border)', background: m.direction === 'inbound' ? 'var(--teal-dim)' : 'transparent' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 4 }}>
                          <span style={{ fontWeight: 700, color: m.direction === 'inbound' ? 'var(--teal)' : 'var(--gray-100)' }}>
                            {m.direction === 'inbound' ? (nameOf(m.from) || 'Agent') : 'You'}
                            {m.status === 'simulated' && <span style={{ color: 'var(--gold)', fontWeight: 600 }}> · not sent</span>}
                          </span>
                          <span style={{ color: 'var(--gray-500)' }}>{fmtStamp(m.sent_at)}</span>
                        </div>
                        <div style={{ fontSize: 12.5, color: 'var(--gray-100)', whiteSpace: 'pre-wrap', lineHeight: 1.5, maxHeight: 220, overflowY: 'auto' }}>{m.body}</div>
                        {m.attachments.length > 0 && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                            {m.attachments.map(a => (
                              <a key={a.id} href={`/api/documents/${a.id}/download`} style={{ fontSize: 11, color: 'var(--teal)', border: '1px solid var(--navy-border)', borderRadius: 5, padding: '2px 7px', textDecoration: 'none' }}>📎 {a.filename}</a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {t.can_reply && connection.connected && (
                      <div style={{ padding: '8px 11px', borderTop: '1px solid var(--navy-border)', display: 'flex', gap: 6 }}>
                        <button onClick={() => setCompose({ mode: 'reply', threadId: t.thread_id, to: last.direction === 'inbound' ? last.from : last.to })} style={btnSm}>Reply</button>
                        {!t.awaiting_reply && (
                          <button onClick={() => setCounterThread(t.thread_id)} style={btnSm}>Counter with AI</button>
                        )}
                        {t.awaiting_reply && !openFollowups.some(f => f.gmail_thread_id === t.thread_id) && (
                          <button onClick={() => {
                            const d = new Date(); d.setDate(d.getDate() + 3); d.setHours(9, 0, 0, 0);
                            act(`/api/gmail/deals/${deal.id}/followups`, { method: 'POST', body: JSON.stringify({ thread_id: t.thread_id, due_at: d.toISOString() }) });
                          }} style={btnSm}>Remind me in 3 days</button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {counterThread && (
        <NegotiationModal
          deal={deal}
          dealId={deal.id}
          threadId={counterThread}
          onClose={() => { setCounterThread(null); load(); }}
          onSent={() => { load(); onChanged && onChanged(); }}
        />
      )}
      {compose && (
        <GmailComposeModal
          deal={deal}
          mode={compose.mode}
          threadId={compose.threadId}
          followup={compose.followup}
          initialTo={compose.to ? (compose.to.match(/<([^>]+)>/)?.[1] || compose.to) : undefined}
          onClose={() => { setCompose(null); load(); }}
          onSent={() => { load(); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}

const card = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: '18px 20px' };
const h2 = { fontSize: 15, fontWeight: 700, color: 'var(--white)' };
const btnSm = { padding: '5px 10px', borderRadius: 7, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-100)', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };
const link = { color: 'var(--teal)', cursor: 'pointer', fontWeight: 600 };
